import { z } from "zod";

/**
 * Plan request contracts — docs/plans.md §9, §10.1.
 *
 * The tier NAMES and interval names live here because both sides need to say
 * them. The CAPACITIES and the PRICES do not: they are constants in
 * `apps/api/src/plans/catalogue.ts`, deliberately not environment and
 * deliberately not shared client-side as a source of truth, because they are
 * stated numbers under a notice obligation (§9 #6) and a number the client
 * could compose is a number the client could compose WRONGLY.
 *
 * That is also why `consent_text_hash` is here and the disclosure text is not.
 * The server authors the sentence; the client renders exactly what it was given
 * and returns the SHA-256 of what it showed. A mismatch refuses the checkout.
 */
/**
 * THE TIER NAMES, ONCE, FOR EVERYONE.
 *
 * This list had been four lists — here, `apps/api/src/plans/catalogue.ts`, and
 * twice in `apps/web/src/lib/api` — and this copy was the one that never heard
 * about the five-tier ladder (ruling C4). It still named a "studio" rung that
 * no longer exists and knew nothing of `pro5` or `pro20`, and because it is
 * what the request schemas validate against, the server OFFERED both Pro rungs
 * on the plan screen and then refused its own offer with a validation error
 * the moment anybody pressed the button. Lite and Personal worked, so the
 * mismatch stayed invisible for as long as nobody bought the top of the
 * ladder. Reported live, 2026-09-08.
 *
 * So the names live here, in the package both sides already depend on, and
 * everything else imports them. A rung added in one place and not another is
 * now a type error rather than a 400 nobody can read.
 */
export const PLAN_TIERS = ["free", "lite", "personal", "pro5", "pro20"] as const;
export type PlanTierName = (typeof PLAN_TIERS)[number];

/** Every tier that can be bought — the ladder without its bottom rung. */
export const PAID_PLAN_TIERS = ["lite", "personal", "pro5", "pro20"] as const;
export type PaidPlanTier = (typeof PAID_PLAN_TIERS)[number];

export const PLAN_BILL_INTERVALS = ["monthly", "annual"] as const;
export type PlanBillInterval = (typeof PLAN_BILL_INTERVALS)[number];

/**
 * The two screens that start a checkout: the billing panel's plans view (which
 * every door, paywall and thread opens rather than selling in place) and the
 * onboarding wizard's trial step.
 */
export const PLAN_CHECKOUT_SURFACES = ["billing", "onboarding"] as const;
export type PlanCheckoutSurface = (typeof PLAN_CHECKOUT_SURFACES)[number];

/**
 * `POST /plan/subscribe`. The three facts the consent is ABOUT, and the hash of
 * the sentence that stated them. `first_charge_on` is an ISO date because it is
 * one of §9 #1's five required facts and therefore part of the hashed text — a
 * buyer shown one first-charge date cannot be bound by a checkout built around
 * another.
 */
export const PlanSubscribeRequest = z.object({
  tier: z.enum(PAID_PLAN_TIERS),
  interval: z.enum(PLAN_BILL_INTERVALS),
  first_charge_on: z.string().min(1),
  consent_text_hash: z.string().min(1),
  /** Relative path the checkout returns the browser to; the server validates
   * the shape and falls back to the plans room. The wizard sends "/". */
  return_to: z.string().min(1).max(200).optional(),
  /**
   * SHA-256 of the UK/EEA withdrawal-right control, when one was shown (ToU 2.1
   * §8.6). Optional on the wire and NOT optional in effect: the server decides
   * from the account's recorded jurisdiction whether it is required, and a
   * UK/EEA checkout without a matching hash is refused with CONSENT_MISMATCH.
   * Optional here because a client that could be asked for it by the schema
   * would be a client the schema had told whether it applied.
   */
  withdrawal_waiver_hash: z.string().min(1).max(200).optional(),
  /**
   * SHA-256 of the drawdown disclosure rendered beside the subscription's
   * (Terms 2.11 §5.3, §8.7). Required: every purchase and trial start carries
   * it, in every tax state, and the server refuses a mismatch.
   */
  drawdown_hash: z.string().min(1).max(200),
  /**
   * Which screen started the checkout, for the refusal event and nothing else
   * (wave 103): a refusal nobody can place is one nobody can diagnose. It
   * decides nothing — the consent is judged the same whichever screen sent it.
   */
  surface: z.enum(PLAN_CHECKOUT_SURFACES).optional(),
});
export type PlanSubscribeRequest = z.infer<typeof PlanSubscribeRequest>;

/**
 * WHEN A TRIAL'S CHANGE HAPPENS, AND IT IS NEVER GUESSED (ruling R-0909-1).
 *
 * Choosing another rung while a trial is running has two honest answers and no
 * default: end the trial today and start the new rung now, or let the trial run
 * its course and start the new rung when it ends. The owner, on staging: picking
 * a rung mid-trial "guesses what they want". So the field is REQUIRED while
 * trialing and REFUSED otherwise — there is no third value meaning "you decide".
 */
export const PLAN_CHANGE_WHEN = ["now", "at_trial_end"] as const;
export type PlanChangeWhen = (typeof PLAN_CHANGE_WHEN)[number];

/**
 * `POST /plan/change` — an upgrade or a downgrade (§7). It is a fresh consent,
 * because it changes the recurring amount, so it carries the same hash the
 * first purchase did and is refused the same way on a mismatch.
 */
export const PlanChangeRequest = z.object({
  tier: z.enum(PAID_PLAN_TIERS),
  interval: z.enum(PLAN_BILL_INTERVALS),
  first_charge_on: z.string().min(1),
  consent_text_hash: z.string().min(1),
  /** Trialing only, and required there (ruling R-0909-1). */
  when: z.enum(PLAN_CHANGE_WHEN).optional(),
});
export type PlanChangeRequest = z.infer<typeof PlanChangeRequest>;

/**
 * `GET /plan/change/preview` — what the confirmation dialog says BEFORE
 * anything is changed (ruling R-0909-1). A read, so it takes a tier and an
 * interval and nothing else; every figure in the answer is composed on the
 * server, because a price the client computed is a price nobody disclosed.
 */
export const PlanChangePreviewQuery = z.object({
  tier: z.enum(PAID_PLAN_TIERS),
  interval: z.enum(PLAN_BILL_INTERVALS),
});
export type PlanChangePreviewQuery = z.infer<typeof PlanChangePreviewQuery>;

/**
 * PAY AS YOU GO (ruling R-0923-3 (2)): turn it on, change the maximum, turn it
 * off. Each is a consent, so each carries the hash of the server-authored
 * sentence it was shown — and, on the way on, of the drawdown sentence shown
 * beside it. The maximum is in whole cents of whole dollars; its range is the
 * server's, because it moves with the tier.
 */
export const PAYG_ACTIONS = ["on", "change", "off"] as const;
export type PaygAction = (typeof PAYG_ACTIONS)[number];

export const PaygRequest = z.object({
  action: z.enum(PAYG_ACTIONS),
  max_cents: z.number().int().positive().optional(),
  consent_text_hash: z.string().min(1).max(200),
  drawdown_hash: z.string().min(1).max(200).optional(),
});
export type PaygRequest = z.infer<typeof PaygRequest>;

/** `GET /plan/payg/consent` — the sentence a maximum would be agreed under. A read. */
export const PaygConsentQuery = z.object({
  action: z.enum(PAYG_ACTIONS),
  max_cents: z.coerce.number().int().positive().optional(),
});
export type PaygConsentQuery = z.infer<typeof PaygConsentQuery>;

/**
 * THE BILLING PORTAL (wave 87): which page it opens on — the card, or the
 * customer's details (name, billing address, billing email, tax ID). Nothing
 * else: the portal cannot cancel or change a plan (docs/plans.md §9 #4).
 */
export const BILLING_PORTAL_FLOWS = ["payment_method", "details"] as const;
export const BillingPortalRequest = z.object({ flow: z.enum(BILLING_PORTAL_FLOWS) });
export type BillingPortalRequest = z.infer<typeof BillingPortalRequest>;

/** A page of the invoices and receipts: the cursor the page before answered with. */
export const InvoicesQuery = z.object({ cursor: z.string().max(200).optional() });
export type InvoicesQuery = z.infer<typeof InvoicesQuery>;
